import useCompany from "../../../hooks/useCompany";
import { ClipLoader } from "react-spinners";
import LogoEditModal from "./modals/LogoEditModal";
import BackgroundEditModal from "./modals/BackgroundEditModal";
import ColorEditModal from "./modals/ColorEditModal";
import FontSettingsModal from "./modals/FontSettingsModal";
import PreviewModal from "./modals/PreviewModal";
import MobilePreviewDrawer from "./MobilePreviewDrawer";
import DesignCardPreview from "./DesignCardPreview";
import { useEffect, useState, useRef } from "react";
import toast from "react-hot-toast";
import useModal from "../../../hooks/useModal";
import ConfirmModal from "../../../components/ConfirmModal";
import useMultimedia from "../../../hooks/useMultimedia";
import { Upload, Palette, Paintbrush, MoreHorizontal, LaptopMinimal, Smartphone } from "lucide-react";

export default function WalletBuilder() {
  const { activeCompany: company, isActiveCompanyPending, updateCompany } = useCompany();
  const { upload } = useMultimedia();
  const [companyData, setCompanyData] = useState(company || {});
  const confirmModalRef = useRef(null);
  
  const logoModal = useModal("editLogo");
  const bgModal = useModal("editBackground");
  const colorModal = useModal("editColors");
  const popupModal = useModal("editPopUp");
  const previewModal = useModal("livePreview");
  const mobilePreviewDrawer = useModal("mobilePreview");

  useEffect(() => {
    setCompanyData(company || {});
  }, [company?.id]);

  if (isActiveCompanyPending || !company) {
    return (
      <div className="flex items-center justify-center w-full h-screen">
        <ClipLoader />
      </div>
    );
  }

  const handleResetToDefault = () => {
    confirmModalRef.current?.open({
      title: "Reset to Default Settings",
      text: "Are you sure you want to reset all design settings to default? This action cannot be undone.",
      onConfirm: confirmReset,
    });
  };

  const confirmReset = async () => {
    try {
      const defaultData = {
        logo: "",
        backgroundImage: "",
        backgroundColor: "#3B82F6",
        backgroundImageOpacity: 1,
        primaryColor: "#2B36D9",
        secondaryColor: "#E3E3E3",
        accentColor: "#2B36D9",
        primaryTextColor: "#000000",
        secondaryTextColor: "#000000",
        linkColor: "#0000FF",
        iframeBackgroundColor: "#FFFFFF",
        // Agregar campos requeridos que no pueden ser null
        accentTextColor: '#FFFFFF',
        secondaryButtonTextColor: '#000000',
        primaryFont: '',
        secondaryFont: '',
        iframeBackgroundOpacity: 1,
        recoveryPasswordTemplateId: '',
        pinTemplateId: '',
      };
      await updateCompany(company.id, defaultData);
      setCompanyData(defaultData);
      toast.success("Reset to default settings");
    } catch (error) {
      console.error("Error resetting:", error);
      toast.error("Error resetting settings");
    }
  };

  const handleSaveFromModal = async (data) => {
    try {
      const stringFields = new Set([
        "logo",
        "backgroundImage",
        "backgroundColor",
        "primaryColor",
        "secondaryColor",
        "accentColor",
        "primaryTextColor",
        "secondaryTextColor",
        "accentTextColor",
        "secondaryButtonTextColor",
        "linkColor",
        "iframeBackgroundColor",
        "primaryFont",
        "secondaryFont",
        "recoveryPasswordTemplateId",
        "pinTemplateId",
      ]);
      const numberFields = new Set([
        "backgroundImageOpacity",
        "iframeBackgroundOpacity",
      ]);

      const normalizedIncoming = {};
      for (const [key, value] of Object.entries(data || {})) {
        if (stringFields.has(key)) {
          normalizedIncoming[key] = typeof value === "string" ? value : "";
          continue;
        }
        if (numberFields.has(key)) {
          const parsed = Number(value);
          normalizedIncoming[key] = Number.isFinite(parsed)
            ? Math.max(0, Math.min(1, parsed))
            : 1;
        }
      }

      const isDataImageUrl = (value) =>
        typeof value === "string" && value.startsWith("data:image/");

      const dataUrlToFile = async (dataUrl, namePrefix) => {
        const response = await fetch(dataUrl);
        const blob = await response.blob();
        const extension = blob.type?.split("/")[1] || "png";
        return new File([blob], `${namePrefix}-${Date.now()}.${extension}`, { type: blob.type || "image/png" });
      };

      // Migrate big base64 payloads to uploaded URLs before persisting.
      if (isDataImageUrl(normalizedIncoming.logo)) {
        const logoFile = await dataUrlToFile(normalizedIncoming.logo, "logo");
        normalizedIncoming.logo = await upload(logoFile);
      }
      if (isDataImageUrl(normalizedIncoming.backgroundImage)) {
        const bgFile = await dataUrlToFile(normalizedIncoming.backgroundImage, "background");
        normalizedIncoming.backgroundImage = await upload(bgFile);
      }

      // If there is no image present, omit opacity to avoid backend rejections.
      const effectiveBackgroundImage = "backgroundImage" in normalizedIncoming
        ? normalizedIncoming.backgroundImage
        : (companyData?.backgroundImage || "");
      if (!effectiveBackgroundImage?.trim()) {
        delete normalizedIncoming.backgroundImageOpacity;
      }

      // Send only fields that actually changed to reduce payload size.
      const safeFields = Object.fromEntries(
        Object.entries(normalizedIncoming).filter(([key, value]) => {
          const currentValue = companyData?.[key];
          return currentValue !== value;
        }),
      );

      if (Object.keys(safeFields).length === 0) {
        toast("No changes to save");
        return;
      }

      console.log('[handleSaveFromModal] Payload to updateCompany:', {
        organizationId: company.id,
        data: safeFields
      });
      const result = await updateCompany(company.id, safeFields);

      if (result?.error) {
        const serverMessage =
          result.error?.message ||
          result.error?.statusText ||
          result.error?.error?.message ||
          "Unknown update error";
        console.error("[handleSaveFromModal] updateCompany failed:", result.error);
        throw new Error(serverMessage);
      }

      setCompanyData((prev) => ({ ...prev, ...safeFields }));
      toast.success("Settings updated successfully!");
    } catch (error) {
      console.error("Error updating settings:", error);
      toast.error(error?.message || "Error updating settings");
    }
  };

  return (
    <div 
      className={`p-6 space-y-6 transition-all duration-300 ${
        mobilePreviewDrawer.isOpen ? 'mr-[400px]' : ''
      }`}
    >
      {/* Header with Live Preview Button */}
      <div className="flex justify-between items-center">
        <div>
          <div className="text-2xl font-semibold mb-2">Customize</div>
          <p className="text-gray-500 text-sm">Add the logo and your color scheme you want to appear in your app.</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => previewModal.open()}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition cursor-pointer"
          >
            <LaptopMinimal className="w-5 h-5" />
          </button>
          <button
            onClick={() => mobilePreviewDrawer.open()}
            className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg font-semibold hover:bg-black/70 transition cursor-pointer"
          >
            <Smartphone className="w-5 h-5" />
          </button>
        </div>
      </div>
        
        <div className="grid gap-6 xl:grid-cols-2 grid-cols-1">
          <DesignCardPreview
            title="Background Settings"
            description="Configure the wallet and iframe screen background style"
            icon={<Palette className="w-5 h-5" />}
            onEdit={() => bgModal.open()}
          >
            <div className="flex gap-2 items-center">
              {/* Wallet Background - Imagen o Color */}
              {companyData?.backgroundImage && companyData.backgroundImage.trim() !== ""
                ? <img src={companyData.backgroundImage} alt="Background" className="max-h-20 max-w-32 object-contain rounded-lg" />
                : <div className="h-20 w-20 rounded-lg flex items-center justify-center text-xs font-semibold" style={(() => {
                    const bg = companyData?.backgroundColor || '#3B82F6';
                    const hex = bg.replace('#', '');
                    const r = parseInt(hex.substring(0, 2), 16);
                    const g = parseInt(hex.substring(2, 4), 16);
                    const b = parseInt(hex.substring(4, 6), 16);
                    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
                    return { backgroundColor: bg, color: brightness > 128 ? '#000000' : '#FFFFFF' };
                  })()}>
                    Background
                  </div>
              }
              {/* Iframe Background Color - Siempre visible */}
              <div className="h-20 w-20 rounded-lg flex items-center justify-center text-xs font-semibold" style={(() => {
                const bg = companyData?.iframeBackgroundColor || '#FFFFFF';
                const hex = bg.replace('#', '');
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);
                const brightness = (r * 299 + g * 587 + b * 114) / 1000;
                return { backgroundColor: bg, color: brightness > 128 ? '#000000' : '#FFFFFF' };
              })()}>
                Iframe
              </div>
            </div>
          </DesignCardPreview>
          
          <DesignCardPreview
            title="Logo Settings"
            description="Configure the wallet login screen logo style"
            icon={<Upload className="w-5 h-5" />}
            onEdit={() => logoModal.open()}
          >
            {companyData?.logo && companyData.logo.trim() !== "" ? (
              <img src={companyData.logo} alt="Logo" className="max-h-20 max-w-full object-contain" />
            ) : (
              <img src="/images/placeholder.png" alt="placeholder" className="h-20 w-20 object-contain opacity-60" />
            )}
          </DesignCardPreview>


          <DesignCardPreview
            title="Wallet Color Settings"
            description="Configure the colors for your wallet"
            icon={<Paintbrush className="w-5 h-5" />}
            onEdit={() => colorModal.open()}
          >
            <div className="flex -space-x-8">
              <div
                className="w-14 h-14 rounded-full shadow"
                style={{ backgroundColor: companyData?.primaryColor || "#0000FF" }}
                title="Primary Color"
              />
              <div
                className="w-14 h-14 rounded-full shadow"
                style={{ backgroundColor: companyData?.secondaryColor || "#FFFFFF" }}
                title="Secondary Color"
              />
              <div
                className="w-14 h-14 rounded-full shadow"
                style={{ backgroundColor: companyData?.accentColor || "#0000FF" }}
                title="Accent Color"
              />
              <div
                className="w-14 h-14 rounded-full shadow"
                style={{ backgroundColor: companyData?.primaryTextColor || "#FFFFFF" }}
                title="Primary Text Color"
              />
              <div
                className="w-14 h-14 rounded-full shadow"
                style={{ backgroundColor: companyData?.secondaryTextColor || "#000000" }}
                title="Secondary Text Color"
              />
              <div
                className="w-14 h-14 rounded-full shadow"
                style={{ backgroundColor: companyData?.accentTextColor || "#FFFFFF" }}
                title="Accent Text Color"
              />
              <div
                className="w-14 h-14 rounded-full shadow"
                style={{ backgroundColor: companyData?.secondaryButtonTextColor || "#000000" }}
                title="Secondary Button Text Color"
              />
              <div
                className="w-14 h-14 rounded-full shadow"
                style={{ backgroundColor: companyData?.linkColor || "#0000FF" }}
                title="Link Color"
              />
            </div>
          </DesignCardPreview>

          <DesignCardPreview
            title="Font Settings"
            description="Choose your primary and secondary fonts."
            icon={<MoreHorizontal className="w-5 h-5" />}
            onEdit={() => popupModal.open()}
          >
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <span className="font-semibold" style={{ fontFamily: companyData?.primaryFont || 'inherit' }}>Primary font:</span>
                <div 
                  style={{ fontFamily: companyData?.primaryFont || 'inherit' }}
                  className="text-base flex-1 p-2 bg-gray-100 rounded"
                >
                  {companyData?.primaryFont || 'Not set'}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-semibold" style={{ fontFamily: companyData?.secondaryFont || 'inherit' }}>Secondary font:</span>
                <div 
                  style={{ fontFamily: companyData?.secondaryFont || 'inherit' }}
                  className="text-base flex-1 p-2 bg-gray-100 rounded"
                >
                  {companyData?.secondaryFont || 'Not set'}
                </div>
              </div>
            </div>
          </DesignCardPreview>
        </div>

        {/* Reset Button */}
        <div className="flex gap-3 justify-center mt-8">
          <button 
            onClick={handleResetToDefault}
            className="px-6 py-2 bg-gray-300 text-gray-900 rounded-full font-semibold hover:bg-gray-400 transition cursor-pointer"
          >
            Reset to Default
          </button>
        </div>

      {/* Modals */}
      <LogoEditModal company={companyData} onSave={handleSaveFromModal} />
      <BackgroundEditModal company={companyData} onSave={handleSaveFromModal} />
      <ColorEditModal company={companyData} onSave={handleSaveFromModal} />
      <FontSettingsModal company={companyData} onSave={handleSaveFromModal} />
      <PreviewModal companyData={companyData} companyId={company?.id} />
      <MobilePreviewDrawer companyId={company?.id} companyData={companyData} />
      <ConfirmModal ref={confirmModalRef} />
    </div>
  )
}
